import { useDeferredValue, useEffect, useMemo, useState } from 'react'
import {
  createColumnHelper,
  flexRender,
  type CellContext,
  type PaginationState,
  type SortingState,
  type TableMeta,
  useTable
} from '@tanstack/react-table'
import { Link, useNavigate, useSearchParams } from 'react-router'
import {
  AnalysisTypeApprovalStatusTag,
  ArrowCounterClockwiseIcon,
  ArrowRightIcon,
  CalendarDotsIcon,
  EmptyState,
  FilterDrawer,
  FunnelIcon,
  getTaskProgressText,
  getCurrentDate,
  getSubstanceText,
  MapPinIcon,
  Mtds,
  paginatedTableFeatures,
  PlanStatusTag,
  ScreenHeader,
  toLocaleDateString,
  TopBar,
  AppLayout,
  useGetPlansQuery,
  useGetWorkspacesQuery,
  WarningIcon,
  XCircleIcon
} from 'prove-common'
import { AppLogo } from '../components/app-logo/AppLogo'
import { CircularProgress } from '../components/circular-progress/CircularProgress'
import { SideBarLayout } from '../components/side-bar-layout/SideBarLayout'
import { useTaskOverviewRows } from '../hooks/usePlanTasks'
import {
  applyTaskViewStateToParams,
  getApprovalSortRank,
  getCompletionRatio,
  getDeadlineColor,
  getFilterCount,
  formatPlanChain,
  getPlanChain,
  getOverdueDeadlineRows,
  getUpcomingDeadlineRows,
  getVisibleRows,
  matchesFilters,
  parsePositiveInt,
  parseTaskFilterState,
  parseTaskStatusFilter,
  TASK_URL_PARAMS,
  type ApprovalFilter,
  type DeadlineFilter,
  type PlanStatusFilter,
  type TaskFilterState,
  type TaskOverviewRow,
  type TaskStatusFilter
} from '../utils/tasksTableUtils'
import {
  collectRecursiveWorkspacePlanIds,
  getWorkspaceById,
  parseWorkspaceId,
  toWorkspaceViewModels,
  useIsWorkspaceFeatureEnabled,
  WORKSPACE_QUERY_PARAM
} from '../workspaces/workspaces'

type FilterOption = { value: string; label: string }

const tableCellSpacingClassName = 'px-4 py-3'
const taskCellClassName = `${tableCellSpacingClassName} min-w-80 max-w-xl whitespace-normal align-top`
const planCellClassName = `${tableCellSpacingClassName} min-w-64 max-w-sm whitespace-normal align-top`
const compactCellClassName = `${tableCellSpacingClassName} whitespace-nowrap align-top`
const actionsCellClassName = 'pl-4 pr-8 py-3 whitespace-nowrap align-middle'
const wrappingColumnIds = new Set(['plan', 'task'])

const isCompactColumn = (columnId: string): boolean =>
  !wrappingColumnIds.has(columnId)

const getTableCellClassName = (columnId: string): string => {
  if (columnId === 'task') return taskCellClassName
  if (columnId === 'plan') return planCellClassName
  if (columnId === 'actions') return actionsCellClassName

  return compactCellClassName
}

const paginationOptions = [
  { value: '10', label: 'Vis 10 pr. side' },
  { value: '20', label: 'Vis 20 pr. side' },
  { value: '50', label: 'Vis 50 pr. side' },
  { value: '100', label: 'Vis 100 pr. side' },
  { value: '200', label: 'Vis 200 pr. side' }
]

const deadlineHorizonOptions = [
  { value: '7', label: 'Neste 7 dager' },
  { value: '14', label: 'Neste 14 dager' },
  { value: '30', label: 'Neste 30 dager' },
  { value: '60', label: 'Neste 60 dager' },
  { value: '90', label: 'Neste 90 dager' }
]

const deadlineOptions: { value: DeadlineFilter; label: string }[] = [
  { value: 'all', label: 'Velg frist' },
  { value: 'upcoming', label: 'Kommende frister' },
  { value: 'today', label: 'Frist i dag' },
  { value: 'overdue', label: 'Utgåtte frister' },
  { value: 'none', label: 'Uten frist' }
]

const approvalOptions: { value: ApprovalFilter; label: string }[] = [
  { value: 'all', label: 'Velg godkjenning' },
  { value: 'approved', label: 'Godkjent' },
  { value: 'awaiting', label: 'Avventer godkjenning' },
  { value: 'declined', label: 'Avvist' }
]

const planStatusOptions: { value: PlanStatusFilter; label: string }[] = [
  { value: 'all', label: 'Velg planstatus' },
  { value: 'active', label: 'Aktive' },
  { value: 'draft', label: 'Utkast' },
  { value: 'deactivated', label: 'Deaktivert' }
]

const getOptionLabel = (options: FilterOption[], value: string): string =>
  options.find((option) => option.value === value)?.label ?? value

const toSortedOptions = (options: FilterOption[]): FilterOption[] =>
  Array.from(new Map(options.map((option) => [option.value, option])).values())
    .filter((option) => option.value.length > 0 && option.label.length > 0)
    .sort((a, b) => a.label.localeCompare(b.label, 'nb-NO'))

const getDateParts = (date: string): { day: string; month: string } => {
  const dateObject = new Date(date)

  return {
    day: dateObject.toLocaleDateString('nb-NO', {
      timeZone: 'Europe/Oslo',
      day: '2-digit'
    }),
    month: dateObject.toLocaleDateString('nb-NO', {
      timeZone: 'Europe/Oslo',
      month: 'short'
    })
  }
}

const TaskProgressSummary = ({ row }: { row: TaskOverviewRow }) => (
  <Mtds.Grid data-gap='1'>
    <Mtds.Flex data-align='center' data-justify='space-between' data-gap='2'>
      <Mtds.Muted>Prøver tatt</Mtds.Muted>
      <Mtds.Muted data-nowrap>
        {row.task.totalCount ?? 0} av {row.task.quantity ?? 0}
      </Mtds.Muted>
    </Mtds.Flex>
    <Mtds.Progress
      value={row.task.totalCount ?? 0}
      max={row.task.quantity ?? 0}
    />
  </Mtds.Grid>
)

const TaskTitle = ({ row }: { row: TaskOverviewRow }) => {
  if (row.task.title?.trim()) return row.task.title

  const product = row.task.analysisType.sampleType?.product
  const matrix = row.task.analysisType.sampleType?.matrix

  if (product == null && matrix == null) return row.title

  return (
    <>
      {product != null && <>{product} &gt; </>}
      {matrix}
    </>
  )
}

const getTaskHref = (row: TaskOverviewRow): string | undefined =>
  row.primaryPlan == null
    ? undefined
    : `/plans/${row.primaryPlan.id}/tasks/${row.id}`

const getCompletionStatusLabel = (row: TaskOverviewRow): string =>
  row.isCompleted ? 'Ferdig' : 'Ikke ferdig'

const getCompletionStatusColor = (
  row: TaskOverviewRow
): 'success' | 'warning' => (row.isCompleted ? 'success' : 'warning')

const TaskDeadlineTag = ({
  row,
  today
}: {
  row: TaskOverviewRow
  today: string
}) => {
  if (row.nearestDuedate == null) {
    return <Mtds.Tag data-color='neutral'>Ingen frist</Mtds.Tag>
  }

  return (
    <Mtds.Tag data-color={getDeadlineColor(row.nearestDuedate, today)}>
      <CalendarDotsIcon aria-hidden />
      {toLocaleDateString(row.nearestDuedate)}
    </Mtds.Tag>
  )
}

const UpcomingDeadlineCard = ({
  row,
  today
}: {
  row: TaskOverviewRow
  today: string
}) => {
  const dateParts = getDateParts(row.nearestDuedate ?? today)
  const content = (
    <Mtds.Flex data-gap='4' data-nowrap>
      <Mtds.Group
        data-color={getDeadlineColor(row.nearestDuedate, today)}
        data-pad='3'
        data-radius='md'
        data-tooltip={
          row.nearestDuedate == null
            ? undefined
            : `Frist ${toLocaleDateString(row.nearestDuedate)}`
        }
        tabIndex={row.nearestDuedate == null ? undefined : 0}
        className='min-w-16 self-start text-center'
      >
        <Mtds.Grid data-gap='0'>
          <strong>{dateParts.day}</strong>
          <Mtds.Muted>{dateParts.month}</Mtds.Muted>
        </Mtds.Grid>
      </Mtds.Group>
      <Mtds.Grid data-gap='2' className='min-w-0 content-start'>
        <Mtds.Heading as='h4' data-size='xs'>
          <TaskTitle row={row} />
        </Mtds.Heading>
        <Mtds.Muted>
          {formatPlanChain(row.planChain) ?? 'Ukjent plan'}
        </Mtds.Muted>
        <TaskProgressSummary row={row} />
      </Mtds.Grid>
    </Mtds.Flex>
  )

  const taskHref = getTaskHref(row)
  if (taskHref == null) return <Mtds.Card>{content}</Mtds.Card>

  return (
    <Mtds.Card as={Link} to={taskHref} className='no-underline'>
      {content}
    </Mtds.Card>
  )
}

const WorkspaceScopeNotice = ({
  workspaceName,
  workspaceId,
  onClear
}: {
  workspaceName: string
  workspaceId: number
  onClear: () => void
}) => (
  <Mtds.Flex
    data-align='center'
    data-justify='space-between'
    data-gap='3'
    data-wrap
  >
    <Mtds.Flex data-align='center' data-gap='2' data-wrap>
      <FunnelIcon aria-hidden />
      <Mtds.Muted>Viser kun oppgaver i</Mtds.Muted>
      <strong>{workspaceName}</strong>
    </Mtds.Flex>
    <Mtds.Flex data-align='center' data-gap='1' data-wrap>
      <Mtds.Button
        as={Link}
        to={`/plans?${WORKSPACE_QUERY_PARAM}=${workspaceId}`}
        data-variant='tertiary'
        data-size='sm'
      >
        <ArrowCounterClockwiseIcon />
        Tilbake til arbeidsområdet
      </Mtds.Button>
      <Mtds.Button
        type='button'
        data-variant='tertiary'
        data-size='sm'
        onClick={onClear}
      >
        <XCircleIcon />
        Vis alle oppgaver
      </Mtds.Button>
    </Mtds.Flex>
  </Mtds.Flex>
)

const DeadlineCardGroup = ({
  title,
  rows,
  today
}: {
  title: string
  rows: TaskOverviewRow[]
  today: string
}) => {
  if (rows.length === 0) return null

  return (
    <Mtds.Grid data-gap='3' className='min-w-0'>
      <Mtds.Heading as='h3' data-size='2xs'>
        {title}
      </Mtds.Heading>
      <Mtds.Flex data-gap='4' data-wrap data-align='start'>
        {rows.map((row) => (
          <div key={row.id} className='w-full max-w-full sm:w-96'>
            <UpcomingDeadlineCard row={row} today={today} />
          </div>
        ))}
      </Mtds.Flex>
    </Mtds.Grid>
  )
}

const UpcomingDeadlines = ({
  rows,
  today
}: {
  rows: TaskOverviewRow[]
  today: string
}) => {
  const [daysAhead, setDaysAhead] = useState(30)
  const overdueRows = getOverdueDeadlineRows(
    rows,
    today,
    Number.MAX_SAFE_INTEGER
  )
  const upcomingRows = getUpcomingDeadlineRows(
    rows,
    today,
    Number.MAX_SAFE_INTEGER,
    daysAhead
  )
  const nextDuedate = upcomingRows[0]?.nearestDuedate
  const highlightedRows = upcomingRows.filter(
    (row) => row.nearestDuedate === nextDuedate
  )
  const remainingRows = upcomingRows.filter(
    (row) => row.nearestDuedate !== nextDuedate
  )
  const hasAnything = overdueRows.length > 0 || upcomingRows.length > 0

  return (
    <Mtds.Group as={Mtds.Grid} data-gap='5'>
      <Mtds.Flex data-align='center' data-justify='space-between' data-wrap>
        <Mtds.Flex data-align='center' data-gap='3' data-wrap>
          <Mtds.Heading as='h2' data-size='sm'>
            <CalendarDotsIcon aria-hidden />
            Frister
          </Mtds.Heading>
          {overdueRows.length > 0 && (
            <Mtds.Tag data-color='danger'>
              <WarningIcon aria-hidden />
              {overdueRows.length}{' '}
              {overdueRows.length === 1 ? 'forfalt frist' : 'forfalte frister'}
            </Mtds.Tag>
          )}
          {upcomingRows.length > 0 && (
            <Mtds.Tag data-color='info'>
              {upcomingRows.length}{' '}
              {upcomingRows.length === 1
                ? 'frist i perioden'
                : 'frister i perioden'}
            </Mtds.Tag>
          )}
        </Mtds.Flex>
        <Mtds.Field
          as='select'
          aria-label='Tidshorisont for kommende frister'
          value={daysAhead.toString()}
          onChange={({ target }) => {
            setDaysAhead(Number(target.value))
          }}
          options={deadlineHorizonOptions}
          className='w-48'
        />
      </Mtds.Flex>
      {hasAnything ? (
        <Mtds.Flex data-gap='5' data-wrap data-align='start'>
          <DeadlineCardGroup
            title='Forfalte frister'
            rows={overdueRows}
            today={today}
          />
          <DeadlineCardGroup
            title='Neste frist'
            rows={highlightedRows}
            today={today}
          />
          <DeadlineCardGroup
            title='Senere frister'
            rows={remainingRows}
            today={today}
          />
        </Mtds.Flex>
      ) : (
        <Mtds.Muted>
          Ingen forfalte frister, og ingen kommende frister de neste {daysAhead}{' '}
          dagene.
        </Mtds.Muted>
      )}
    </Mtds.Group>
  )
}

const TaskFilters = ({
  filters,
  setFilters,
  planOptions,
  subPlanOptions,
  productOptions,
  matrixOptions,
  substanceOptions,
  sampntTypeOptions,
  resultCount
}: {
  filters: TaskFilterState
  setFilters: (filters: TaskFilterState) => void
  planOptions: FilterOption[]
  subPlanOptions: FilterOption[]
  productOptions: FilterOption[]
  matrixOptions: FilterOption[]
  substanceOptions: FilterOption[]
  sampntTypeOptions: FilterOption[]
  resultCount: number
}) => (
  <FilterDrawer
    title='Filtrer oppgaver'
    isFiltering={getFilterCount(filters) > 0}
    filterCount={getFilterCount(filters)}
    applyLabel={`Vis ${resultCount} treff`}
    onReset={() => setFilters(parseTaskFilterState(new URLSearchParams()))}
  >
    <Mtds.Grid data-gap='5'>
      <Mtds.Field
        as='input'
        label='Søk'
        value={filters.searchText}
        onChange={({ target }) =>
          setFilters({ ...filters, searchText: target.value })
        }
        placeholder='Søk etter oppgave, analyse eller plan'
      />
      <Mtds.Field
        as='select'
        label='Plan'
        value={filters.planId}
        onChange={({ target }) =>
          setFilters({ ...filters, planId: target.value, subPlanIds: [] })
        }
        options={planOptions}
      />
      {subPlanOptions.length > 0 && (
        <Mtds.Field
          as={Mtds.Field.Suggestion}
          label='Underplan'
          placeholder='Velg underplan'
          selected={subPlanOptions.filter((option) =>
            filters.subPlanIds.includes(option.value)
          )}
          onSelectedChange={(items) =>
            setFilters({
              ...filters,
              subPlanIds: items.map(({ value }) => value)
            })
          }
          options={subPlanOptions}
        />
      )}
      <Mtds.Field
        as={Mtds.Field.Suggestion}
        data-multiple
        label='Produkt'
        placeholder='Velg produkt'
        selected={productOptions.filter((option) =>
          filters.productIds.includes(option.value)
        )}
        onSelectedChange={(items) =>
          setFilters({
            ...filters,
            productIds: items.map(({ value }) => value)
          })
        }
        options={productOptions}
      />
      <Mtds.Field
        as={Mtds.Field.Suggestion}
        data-multiple
        label='Prøve av'
        placeholder='Velg prøve av'
        selected={matrixOptions.filter((option) =>
          filters.matrixIds.includes(option.value)
        )}
        onSelectedChange={(items) =>
          setFilters({
            ...filters,
            matrixIds: items.map(({ value }) => value)
          })
        }
        options={matrixOptions}
      />
      <Mtds.Field
        as={Mtds.Field.Suggestion}
        data-multiple
        label='Analyser for'
        placeholder='Velg analyse'
        selected={substanceOptions.filter((option) =>
          filters.substanceIds.includes(option.value)
        )}
        onSelectedChange={(items) =>
          setFilters({
            ...filters,
            substanceIds: items.map(({ value }) => value)
          })
        }
        options={substanceOptions}
      />
      <Mtds.Field
        as={Mtds.Field.Suggestion}
        data-multiple
        label='Uttakssted'
        placeholder='Velg uttakssted'
        selected={sampntTypeOptions.filter((option) =>
          filters.sampntTypeIds.includes(option.value)
        )}
        onSelectedChange={(items) =>
          setFilters({
            ...filters,
            sampntTypeIds: items.map(({ value }) => value)
          })
        }
        options={sampntTypeOptions}
      />
      <Mtds.Field
        as='select'
        label='Planstatus'
        value={filters.planStatus}
        onChange={({ target }) =>
          setFilters({
            ...filters,
            planStatus: target.value as PlanStatusFilter
          })
        }
        options={planStatusOptions}
      />
      <Mtds.Field
        as='select'
        label='Frist'
        value={filters.deadline}
        onChange={({ target }) =>
          setFilters({ ...filters, deadline: target.value as DeadlineFilter })
        }
        options={deadlineOptions}
      />
      <Mtds.Field
        as='select'
        label='Godkjenning'
        value={filters.approval}
        onChange={({ target }) =>
          setFilters({ ...filters, approval: target.value as ApprovalFilter })
        }
        options={approvalOptions}
      />
    </Mtds.Grid>
  </FilterDrawer>
)

const FilterChips = ({
  filters,
  setFilters,
  planOptions,
  subPlanOptions,
  productOptions,
  matrixOptions,
  substanceOptions,
  sampntTypeOptions
}: {
  filters: TaskFilterState
  setFilters: (filters: TaskFilterState) => void
  planOptions: FilterOption[]
  subPlanOptions: FilterOption[]
  productOptions: FilterOption[]
  matrixOptions: FilterOption[]
  substanceOptions: FilterOption[]
  sampntTypeOptions: FilterOption[]
}) => {
  if (getFilterCount(filters) === 0) return null

  return (
    <Mtds.Flex>
      {filters.searchText.trim().length > 0 && (
        <Mtds.Chip
          data-removable
          onClick={() => setFilters({ ...filters, searchText: '' })}
        >
          Søk: {filters.searchText}
        </Mtds.Chip>
      )}
      {filters.planId !== 'all' && (
        <Mtds.Chip
          data-removable
          onClick={() =>
            setFilters({ ...filters, planId: 'all', subPlanIds: [] })
          }
        >
          {getOptionLabel(planOptions, filters.planId)}
        </Mtds.Chip>
      )}
      {filters.planStatus !== 'all' && (
        <Mtds.Chip
          data-removable
          onClick={() => setFilters({ ...filters, planStatus: 'all' })}
        >
          Planstatus: {getOptionLabel(planStatusOptions, filters.planStatus)}
        </Mtds.Chip>
      )}
      {filters.deadline !== 'all' && (
        <Mtds.Chip
          data-removable
          onClick={() => setFilters({ ...filters, deadline: 'all' })}
        >
          Frist: {getOptionLabel(deadlineOptions, filters.deadline)}
        </Mtds.Chip>
      )}
      {filters.approval !== 'all' && (
        <Mtds.Chip
          data-removable
          onClick={() => setFilters({ ...filters, approval: 'all' })}
        >
          Godkjenning: {getOptionLabel(approvalOptions, filters.approval)}
        </Mtds.Chip>
      )}
      {filters.subPlanIds.map((value) => (
        <Mtds.Chip
          key={`sub-plan-${value}`}
          data-removable
          onClick={() =>
            setFilters({
              ...filters,
              subPlanIds: filters.subPlanIds.filter((id) => id !== value)
            })
          }
        >
          {getOptionLabel(subPlanOptions, value)}
        </Mtds.Chip>
      ))}
      {filters.productIds.map((value) => (
        <Mtds.Chip
          key={`product-${value}`}
          data-removable
          onClick={() =>
            setFilters({
              ...filters,
              productIds: filters.productIds.filter((id) => id !== value)
            })
          }
        >
          {getOptionLabel(productOptions, value)}
        </Mtds.Chip>
      ))}
      {filters.matrixIds.map((value) => (
        <Mtds.Chip
          key={`matrix-${value}`}
          data-removable
          onClick={() =>
            setFilters({
              ...filters,
              matrixIds: filters.matrixIds.filter((id) => id !== value)
            })
          }
        >
          {getOptionLabel(matrixOptions, value)}
        </Mtds.Chip>
      ))}
      {filters.substanceIds.map((value) => (
        <Mtds.Chip
          key={`substance-${value}`}
          data-removable
          onClick={() =>
            setFilters({
              ...filters,
              substanceIds: filters.substanceIds.filter((id) => id !== value)
            })
          }
        >
          {getOptionLabel(substanceOptions, value)}
        </Mtds.Chip>
      ))}
      {filters.sampntTypeIds.map((value) => (
        <Mtds.Chip
          key={`sampnt-${value}`}
          data-removable
          onClick={() =>
            setFilters({
              ...filters,
              sampntTypeIds: filters.sampntTypeIds.filter((id) => id !== value)
            })
          }
        >
          {getOptionLabel(sampntTypeOptions, value)}
        </Mtds.Chip>
      ))}
    </Mtds.Flex>
  )
}

const PlanCell = ({
  row,
  selectedTab
}: {
  row: TaskOverviewRow
  selectedTab: TaskStatusFilter
}) => {
  if (row.mainPlan == null && row.subPlan == null) {
    return <Mtds.Muted>Ukjent plan</Mtds.Muted>
  }

  return (
    <Mtds.Grid data-gap='3'>
      <Mtds.Grid data-gap='1'>
        {row.planChain.length > 0 ? (
          <Link to={`/plans/${row.subPlan?.id ?? row.primaryPlan?.id}`}>
            {formatPlanChain(row.planChain)}
          </Link>
        ) : (
          <Mtds.Muted>Ukjent plan</Mtds.Muted>
        )}
      </Mtds.Grid>
      <Mtds.Flex data-align='center' data-gap='2' data-wrap>
        {row.primaryPlan != null && (
          <PlanStatusTag status={row.primaryPlan.status} />
        )}
        <TaskCompletionStatusTag row={row} selectedTab={selectedTab} />
      </Mtds.Flex>
    </Mtds.Grid>
  )
}

const TaskCell = ({ row }: { row: TaskOverviewRow }) => {
  const taskHref = getTaskHref(row)
  const analysis = getSubstanceText(row.task.analysisType)
  const sampntTypeName = row.task.sampntType.nameNo

  return (
    <Mtds.Grid data-gap='2'>
      {taskHref == null ? (
        <strong>
          <TaskTitle row={row} />
        </strong>
      ) : (
        <Link id={`task-link-${row.id}`} to={taskHref} className='no-underline'>
          <strong>
            <TaskTitle row={row} />
          </strong>
        </Link>
      )}
      <Mtds.Muted>{analysis ?? '-'}</Mtds.Muted>
      {sampntTypeName != null && (
        <Mtds.Muted>
          <span className='inline-flex items-center gap-1'>
            <MapPinIcon aria-hidden />
            {sampntTypeName}
          </span>
        </Mtds.Muted>
      )}
    </Mtds.Grid>
  )
}

const ApprovalCell = ({ row }: { row: TaskOverviewRow }) =>
  row.task.analysisTypeApprovalStatus == null ? (
    <Mtds.Tag data-color='neutral'>Ingen kontrakt</Mtds.Tag>
  ) : (
    <AnalysisTypeApprovalStatusTag
      status={row.task.analysisTypeApprovalStatus}
    />
  )

const TaskCompletionStatusTag = ({
  row,
  selectedTab
}: {
  row: TaskOverviewRow
  selectedTab: TaskStatusFilter
}) => {
  if (selectedTab === 'incomplete' && !row.isCompleted) return null
  if (selectedTab === 'completed' && row.isCompleted) return null

  return (
    <Mtds.Tag data-color={getCompletionStatusColor(row)}>
      {getCompletionStatusLabel(row)}
    </Mtds.Tag>
  )
}

const ProgressCell = ({ row }: { row: TaskOverviewRow }) => (
  <Mtds.Flex data-align='center' data-gap='2' data-nowrap>
    <CircularProgress
      value={row.task.totalCount ?? 0}
      max={row.task.quantity ?? 0}
    />
    <Mtds.Muted data-nowrap>
      {getTaskProgressText(row.task.totalCount, row.task.quantity)}
    </Mtds.Muted>
  </Mtds.Flex>
)

type TaskCellContext = CellContext<
  typeof paginatedTableFeatures,
  TaskOverviewRow,
  unknown
>
type TaskTableMeta = TableMeta<
  typeof paginatedTableFeatures,
  TaskOverviewRow
> & {
  selectedTab?: TaskStatusFilter
  today?: string
}

const getTaskTableMeta = (info: TaskCellContext) =>
  info.table.options.meta as TaskTableMeta | undefined

const renderPlanCell = (info: TaskCellContext) => {
  const selectedTab = getTaskTableMeta(info)?.selectedTab ?? 'incomplete'

  return <PlanCell row={info.row.original} selectedTab={selectedTab} />
}

const renderTaskCell = (info: TaskCellContext) => (
  <TaskCell row={info.row.original} />
)

const renderDeadlineCell = (info: TaskCellContext) => {
  const today = getTaskTableMeta(info)?.today ?? getCurrentDate()

  return <TaskDeadlineTag row={info.row.original} today={today} />
}

const renderProgressCell = (info: TaskCellContext) => (
  <ProgressCell row={info.row.original} />
)

const renderApprovalCell = (info: TaskCellContext) => (
  <ApprovalCell row={info.row.original} />
)

const renderActionsCell = () => (
  <Mtds.Flex data-align='center' data-justify='end'>
    <ArrowRightIcon data-size='lg' />
  </Mtds.Flex>
)

const columnHelper = createColumnHelper<
  typeof paginatedTableFeatures,
  TaskOverviewRow
>()

const taskColumns = columnHelper.columns([
  columnHelper.accessor((row) => formatPlanChain(row.planChain) ?? '', {
    id: 'plan',
    header: 'Plan',
    enableSorting: false,
    cell: renderPlanCell
  }),
  columnHelper.accessor(
    (row) =>
      [
        row.task.analysisType.sampleType?.product,
        row.task.analysisType.sampleType?.matrix,
        getSubstanceText(row.task.analysisType),
        row.task.sampntType.nameNo
      ]
        .filter(Boolean)
        .join(' '),
    {
      id: 'task',
      header: 'Oppgave',
      enableSorting: false,
      cell: renderTaskCell
    }
  ),
  columnHelper.accessor((row) => row.nearestDuedate, {
    id: 'deadline',
    header: 'Frist',
    sortUndefined: 'last',
    cell: renderDeadlineCell
  }),
  columnHelper.accessor(getCompletionRatio, {
    id: 'progress',
    header: 'Prøver tatt',
    cell: renderProgressCell
  }),
  columnHelper.accessor(getApprovalSortRank, {
    id: 'approval',
    header: 'Godkjenning',
    cell: renderApprovalCell
  }),
  columnHelper.display({
    id: 'actions',
    enableSorting: false,
    cell: renderActionsCell
  })
])

export default function TasksScreen() {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const isWorkspaceFeatureEnabled = useIsWorkspaceFeatureEnabled().enabled
  const { data: workspaceData } = useGetWorkspacesQuery(
    isWorkspaceFeatureEnabled
  )
  const workspaces = useMemo(
    () => toWorkspaceViewModels(workspaceData),
    [workspaceData]
  )
  const [selectedTab, setSelectedTab] = useState<TaskStatusFilter>(() =>
    parseTaskStatusFilter(searchParams.get(TASK_URL_PARAMS.tab))
  )
  const [filters, setFilters] = useState<TaskFilterState>(() =>
    parseTaskFilterState(searchParams)
  )
  const [sorting, setSorting] = useState<SortingState>(() => {
    const sortId = searchParams.get(TASK_URL_PARAMS.sortId)

    return sortId == null
      ? []
      : [
          {
            id: sortId,
            desc: searchParams.get(TASK_URL_PARAMS.sortDesc) === 'true'
          }
        ]
  })
  const [pagination, setPagination] = useState<PaginationState>(() => ({
    pageIndex: parsePositiveInt(searchParams.get(TASK_URL_PARAMS.pageIndex), 0),
    pageSize: parsePositiveInt(searchParams.get(TASK_URL_PARAMS.pageSize), 20)
  }))
  const deferredFilters = useDeferredValue(filters)
  const resetPagination = () =>
    setPagination((prev) =>
      prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }
    )
  const updateSelectedTab = (tab: TaskStatusFilter) => {
    setSelectedTab(tab)
    resetPagination()
  }
  const updateFilters = (nextFilters: TaskFilterState) => {
    setFilters(nextFilters)
    resetPagination()
  }
  const {
    rows: allRows,
    today,
    isPendingTasks,
    isFetching: isFetchingTasks,
    isError: isErrorOverview
  } = useTaskOverviewRows()
  const { data: plansData, isPending: isPendingPlans } = useGetPlansQuery()
  const plans = useMemo(() => plansData?.plans ?? [], [plansData?.plans])
  const selectedWorkspaceId = parseWorkspaceId(
    searchParams.get(WORKSPACE_QUERY_PARAM)
  )
  const workspacePlanIds = useMemo(
    () =>
      selectedWorkspaceId == null
        ? undefined
        : collectRecursiveWorkspacePlanIds(plans, selectedWorkspaceId),
    [plans, selectedWorkspaceId]
  )
  const visiblePlans = useMemo(
    () =>
      workspacePlanIds == null
        ? plans
        : plans.filter((plan) => workspacePlanIds.has(plan.id)),
    [plans, workspacePlanIds]
  )
  const planById = useMemo(
    () => new Map(plans.map((plan) => [plan.id, plan])),
    [plans]
  )
  const rows = useMemo(
    () =>
      workspacePlanIds == null
        ? allRows
        : allRows.filter((row) =>
            row.planFilterIds.some((planId) => workspacePlanIds.has(planId))
          ),
    [allRows, workspacePlanIds]
  )

  const filteredRows = useMemo(
    () => rows.filter((row) => matchesFilters(row, deferredFilters, today)),
    [deferredFilters, rows, today]
  )
  const incompleteRows = useMemo(
    () => filteredRows.filter((row) => !row.isCompleted),
    [filteredRows]
  )
  const completedRows = useMemo(
    () => filteredRows.filter((row) => row.isCompleted),
    [filteredRows]
  )
  const visibleRows = useMemo(
    () =>
      getVisibleRows(selectedTab, filteredRows, incompleteRows, completedRows),
    [completedRows, filteredRows, incompleteRows, selectedTab]
  )
  const table = useTable({
    features: paginatedTableFeatures,
    data: visibleRows,
    columns: taskColumns,
    meta: { selectedTab, today } as TaskTableMeta,
    state: {
      pagination,
      sorting
    },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    autoResetPageIndex: false
  })
  useEffect(() => {
    const next = applyTaskViewStateToParams(searchParams, {
      filters: deferredFilters,
      selectedTab,
      pageIndex: pagination.pageIndex,
      pageSize: pagination.pageSize,
      sortId: sorting[0]?.id,
      sortDesc: sorting[0]?.desc
    })

    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true })
    }
  }, [
    deferredFilters,
    pagination.pageIndex,
    pagination.pageSize,
    searchParams,
    selectedTab,
    setSearchParams,
    sorting
  ])
  const planOptions = useMemo(
    () => [
      { value: 'all', label: 'Velg plan' },
      ...visiblePlans
        .filter((plan) => (plan.parents?.length ?? 0) === 0)
        .map((plan) => ({
          value: plan.id.toString(),
          label: plan.description ?? `Plan ${plan.id}`
        }))
        .sort((a, b) => a.label.localeCompare(b.label, 'nb-NO'))
    ],
    [visiblePlans]
  )
  const subPlanOptions = useMemo(() => {
    if (filters.planId === 'all') return []

    const selectedPlanId = Number(filters.planId)

    return toSortedOptions(
      visiblePlans
        .filter((plan) => {
          const planChain = getPlanChain(plan, planById)

          return (
            plan.id !== selectedPlanId &&
            planChain.some((ancestor) => ancestor.id === selectedPlanId)
          )
        })
        .map((plan) => {
          const planChain = getPlanChain(plan, planById)
          const label =
            formatPlanChain(planChain) ?? plan.description ?? `Plan ${plan.id}`

          return {
            value: plan.id.toString(),
            label
          }
        })
    )
  }, [filters.planId, planById, visiblePlans])
  const productOptions = useMemo(
    () =>
      toSortedOptions(
        rows.map((row) => ({
          value: row.productId?.toString() ?? '',
          label: row.task.analysisType.sampleType?.product ?? ''
        }))
      ),
    [rows]
  )
  const matrixOptions = useMemo(
    () =>
      toSortedOptions(
        rows.map((row) => ({
          value: row.matrixId?.toString() ?? '',
          label: row.task.analysisType.sampleType?.matrix ?? ''
        }))
      ),
    [rows]
  )
  const substanceOptions = useMemo(
    () =>
      toSortedOptions(
        rows.flatMap(
          (row) =>
            row.task.analysisType.substances?.map(({ substance }) => ({
              value: substance.id.toString(),
              label: substance.name
            })) ?? []
        )
      ),
    [rows]
  )
  const sampntTypeOptions = useMemo(
    () =>
      toSortedOptions(
        rows.map((row) => ({
          value: row.sampntTypeId?.toString() ?? '',
          label: row.task.sampntType.nameNo ?? ''
        }))
      ),
    [rows]
  )
  const isLoading = isPendingPlans
  const isTasksLoading = isPendingTasks
  const isError = isErrorOverview
  const scopedWorkspace =
    selectedWorkspaceId == null
      ? undefined
      : getWorkspaceById(workspaces, selectedWorkspaceId)
  const scopedWorkspaceName =
    scopedWorkspace != null && scopedWorkspace.id === selectedWorkspaceId
      ? scopedWorkspace.name
      : undefined
  const clearWorkspaceScope = () => {
    const next = new URLSearchParams(searchParams)
    next.delete(WORKSPACE_QUERY_PARAM)
    navigate({ search: next.toString() }, { replace: true })
  }

  return (
    <AppLayout
      useMobileBar={false}
      sidebar={<SideBarLayout />}
      isLoading={isLoading}
      isError={isError}
      topBar={
        <TopBar
          logo={<AppLogo />}
          title='Oppgaver'
          showNotificationBell={true}
        />
      }
      isFullWidth
    >
      {!isLoading && !isError && (
        <Mtds.Grid data-gap='4' className='w-full max-w-6xl mx-auto'>
          <ScreenHeader
            title='Oppgaver'
            hideDescriptionBehindHelpText
            description={
              <p>
                Her finner du oppgavene på tvers av planene dine. Øverst ser du
                de nærmeste fristene, og i listen under kan du filtrere på plan,
                frist og godkjenning.
              </p>
            }
          >
            {scopedWorkspaceName != null && selectedWorkspaceId != null && (
              <WorkspaceScopeNotice
                workspaceName={scopedWorkspaceName}
                workspaceId={selectedWorkspaceId}
                onClear={clearWorkspaceScope}
              />
            )}
          </ScreenHeader>
          {isTasksLoading ? (
            <Mtds.Card>
              <Mtds.Spinner>Henter oppgaver...</Mtds.Spinner>
            </Mtds.Card>
          ) : (
            <>
              <UpcomingDeadlines rows={rows} today={today} />
              <Mtds.Grid data-gap='4'>
                <Mtds.Heading as='h2' data-size='sm'>
                  Alle oppgaver
                </Mtds.Heading>
                <Mtds.Flex
                  data-align='center'
                  data-justify='space-between'
                  data-wrap
                >
                  <Mtds.Togglegroup
                    {...({ focusgroup: 'radiogroup' } as Record<
                      string,
                      string
                    >)}
                    aria-label='Oppgavestatus'
                  >
                    <Mtds.Togglegroup.Item
                      checked={selectedTab === 'incomplete'}
                      name='task-status'
                      value='incomplete'
                      onChange={() => updateSelectedTab('incomplete')}
                    >
                      Ikke ferdig ({incompleteRows.length})
                    </Mtds.Togglegroup.Item>
                    <Mtds.Togglegroup.Item
                      checked={selectedTab === 'completed'}
                      name='task-status'
                      value='completed'
                      onChange={() => updateSelectedTab('completed')}
                    >
                      Ferdig ({completedRows.length})
                    </Mtds.Togglegroup.Item>
                    <Mtds.Togglegroup.Item
                      checked={selectedTab === 'all'}
                      name='task-status'
                      value='all'
                      onChange={() => updateSelectedTab('all')}
                    >
                      Alle ({filteredRows.length})
                    </Mtds.Togglegroup.Item>
                  </Mtds.Togglegroup>
                  <Mtds.Flex data-align='center' data-wrap>
                    {isFetchingTasks && !isPendingTasks && (
                      <Mtds.Spinner>Oppdaterer oppgaver...</Mtds.Spinner>
                    )}
                    <TaskFilters
                      filters={filters}
                      setFilters={updateFilters}
                      planOptions={planOptions}
                      subPlanOptions={subPlanOptions}
                      productOptions={productOptions}
                      matrixOptions={matrixOptions}
                      substanceOptions={substanceOptions}
                      sampntTypeOptions={sampntTypeOptions}
                      resultCount={visibleRows.length}
                    />
                  </Mtds.Flex>
                </Mtds.Flex>
                <FilterChips
                  filters={filters}
                  setFilters={updateFilters}
                  planOptions={planOptions}
                  subPlanOptions={subPlanOptions}
                  productOptions={productOptions}
                  matrixOptions={matrixOptions}
                  substanceOptions={substanceOptions}
                  sampntTypeOptions={sampntTypeOptions}
                />
              </Mtds.Grid>
              {visibleRows.length === 0 ? (
                <EmptyState
                  title={
                    getFilterCount(filters) > 0
                      ? 'Ingen treff'
                      : 'Ingen oppgaver'
                  }
                  description={
                    getFilterCount(filters) > 0
                      ? 'Prøv andre filtervalg.'
                      : 'Det finnes ingen oppgaver i planene ennå.'
                  }
                />
              ) : (
                <Mtds.Grid data-gap='6'>
                  <Mtds.Card className='border-0 bg-transparent p-0'>
                    <div className='overflow-x-auto px-2 py-1'>
                      <Mtds.Table
                        aria-label='Oppgaver'
                        data-mobile='stacked'
                        className='w-full'
                      >
                        <thead>
                          {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id}>
                              {headerGroup.headers.map((header) => (
                                <Mtds.Table.ThSortable
                                  id={header.id}
                                  key={header.id}
                                  className={getTableCellClassName(
                                    header.column.id
                                  )}
                                  data-nowrap={
                                    isCompactColumn(header.column.id) ||
                                    undefined
                                  }
                                  aria-sort={header.column.getIsSorted()}
                                  onClick={
                                    header.column.getCanSort()
                                      ? header.column.getToggleSortingHandler()
                                      : undefined
                                  }
                                >
                                  {flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                  )}
                                </Mtds.Table.ThSortable>
                              ))}
                            </tr>
                          ))}
                        </thead>
                        <tbody>
                          {table.getRowModel().rows.map((row) => {
                            const taskHref = getTaskHref(row.original)

                            return (
                              <tr
                                key={row.id}
                                data-clickdelegatefor={
                                  taskHref == null
                                    ? undefined
                                    : `task-link-${row.original.id}`
                                }
                                className={
                                  taskHref == null
                                    ? undefined
                                    : 'hover:bg-surface-hover cursor-pointer'
                                }
                              >
                                {row.getVisibleCells().map((cell) => (
                                  <td
                                    key={cell.id}
                                    className={getTableCellClassName(
                                      cell.column.id
                                    )}
                                    data-nowrap={
                                      isCompactColumn(cell.column.id) ||
                                      undefined
                                    }
                                  >
                                    {flexRender(
                                      cell.column.columnDef.cell,
                                      cell.getContext()
                                    )}
                                  </td>
                                ))}
                              </tr>
                            )
                          })}
                        </tbody>
                      </Mtds.Table>
                    </div>
                  </Mtds.Card>
                  <Mtds.Flex
                    data-align='center'
                    data-justify='space-between'
                    data-wrap
                  >
                    <p>
                      Viser {table.getRowModel().rows.length} av{' '}
                      {visibleRows.length}
                    </p>
                    <Mtds.Pagination
                      current={table.state.pagination.pageIndex + 1}
                      total={table.getPageCount()}
                      show={table.state.pagination.pageSize}
                      props={({ page }) => ({
                        onClick: () => {
                          globalThis.scrollTo({ top: 0 })
                          table.setPageIndex(page - 1)
                        }
                      })}
                    />
                    <Mtds.Field
                      as='select'
                      aria-label='Antall rader per side'
                      value={table.state.pagination.pageSize.toString()}
                      onChange={({ target }) =>
                        table.setPageSize(Number(target.value))
                      }
                      options={paginationOptions}
                    />
                  </Mtds.Flex>
                </Mtds.Grid>
              )}
            </>
          )}
        </Mtds.Grid>
      )}
    </AppLayout>
  )
}
